import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import { LUXURY_ESCAPES, brands } from '~/consts/brands';

import type { BrandRegionOverrides } from '~/utils/brandRegionOverrides';

export type { BrandRegionOverrides } from '~/utils/brandRegionOverrides';

interface Props {
  open: boolean;
  onRequestClose: () => void;
  onSet: (brandRegionOverrides: BrandRegionOverrides) => void;
  brandRegionOverrides?: BrandRegionOverrides | null;
  name: string;
  label: string;
  saveButton?: string;
}

interface BrandRegionOverrideRow {
  id: string;
  brand: string;
  regions: Array<string>;
}

interface BrandRegionOverrideRowValidation {
  isBrandMissing: boolean;
  isRegionMissing: boolean;
  hasError: boolean;
}

const luxuryEscapesBrand = brands.find((brand) => brand.value === LUXURY_ESCAPES);
const brandRegionOverrideBrands = brands.filter((brand) => brand.value !== LUXURY_ESCAPES);

const brandRegionOptions = brands.reduce<Record<string, Array<{ code: string; name: string }>>>((acc, brand) => {
  const regionBrand = brand.value as Parameters<typeof libRegions.getRegions>[0];

  acc[brand.value] = libRegions.getRegions(regionBrand).map((region) => ({
    code: region.code,
    name: region.name,
  }));
  return acc;
}, {});

function getRegionOptionsForBrand(brand: string): Array<{ code: string; name: string }> {
  return brandRegionOptions[brand] ?? [];
}

function normalizeRegions(regions: Array<string>) {
  return Array.from(new Set(regions.filter(Boolean))).sort((firstRegion, secondRegion) =>
    firstRegion.localeCompare(secondRegion),
  );
}

function getRowValidationState(row: BrandRegionOverrideRow): BrandRegionOverrideRowValidation {
  if (!row.brand) {
    return {
      isBrandMissing: true,
      isRegionMissing: false,
      hasError: true,
    };
  }

  const allowedRegionCodes = new Set(getRegionOptionsForBrand(row.brand).map((region) => region.code));
  const selectedAllowedRegionCount = row.regions.filter((regionCode) => allowedRegionCodes.has(regionCode)).length;
  const isRegionMissing = selectedAllowedRegionCount === 0;

  return {
    isBrandMissing: false,
    isRegionMissing,
    hasError: isRegionMissing,
  };
}

export default function SetBrandRegionOverridesModal({
  open,
  onRequestClose,
  onSet,
  brandRegionOverrides,
  name,
  label,
  saveButton,
}: Props) {
  const rowIdCounter = useRef(0);
  const [rows, setRows] = useState<Array<BrandRegionOverrideRow>>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const nextRowId = useCallback(() => {
    rowIdCounter.current += 1;
    return `brand-region-override-row-${rowIdCounter.current}`;
  }, []);

  const buildRowsFromOverrides = useCallback(
    (overrides: BrandRegionOverrides | null): Array<BrandRegionOverrideRow> => {
      if (!overrides || !Object.keys(overrides).length) {
        return [];
      }

      return Object.entries(overrides).map(([brand, regions]) => {
        return {
          id: nextRowId(),
          brand,
          regions: normalizeRegions(regions ?? []),
        };
      });
    },
    [nextRowId],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setRows(buildRowsFromOverrides(brandRegionOverrides ?? null));
    setHasAttemptedSubmit(false);
  }, [open, brandRegionOverrides, buildRowsFromOverrides]);

  const addBrandEntry = () => {
    if (!canAddBrandEntry) {
      return;
    }

    setRows((previousRows) => {
      return [
        ...previousRows,
        {
          id: nextRowId(),
          brand: '',
          regions: [],
        },
      ];
    });
  };

  const removeBrandEntry = (entryId: string) => {
    setRows((previousRows) => {
      return previousRows.filter((row) => row.id !== entryId);
    });
  };

  const updateBrand = (entryId: string, brand: string) => {
    const allowedRegionCodes = new Set(getRegionOptionsForBrand(brand).map((region) => region.code));

    setRows((previousRows) => {
      return previousRows.map((row) => {
        if (row.id !== entryId) {
          return row;
        }

        return {
          ...row,
          brand,
          regions: row.regions.filter((regionCode) => allowedRegionCodes.has(regionCode)),
        };
      });
    });
  };

  const toggleRegion = (entryId: string, regionCode: string, shouldSelect: boolean) => {
    setRows((previousRows) => {
      return previousRows.map((row) => {
        if (row.id !== entryId) {
          return row;
        }

        const nextRegions = shouldSelect
          ? [...row.regions, regionCode]
          : row.regions.filter((existingRegionCode) => existingRegionCode !== regionCode);

        return {
          ...row,
          regions: normalizeRegions(nextRegions),
        };
      });
    });
  };

  const setAllRegionsForEntry = (entryId: string, shouldSelectAll: boolean) => {
    setRows((previousRows) => {
      return previousRows.map((row) => {
        if (row.id !== entryId) {
          return row;
        }

        const allowedRegionCodes = getRegionOptionsForBrand(row.brand).map((region) => region.code);

        return {
          ...row,
          regions: shouldSelectAll ? allowedRegionCodes : [],
        };
      });
    });
  };

  const selectedBrands = rows.map((row) => row.brand).filter(Boolean);
  const hasInvalidRows = rows.some((row) => getRowValidationState(row).hasError);
  const hasRowsWithoutBrand = rows.some((row) => !row.brand);
  const canAddBrandEntry =
    selectedBrands.filter((brand) => brand !== LUXURY_ESCAPES).length < brandRegionOverrideBrands.length &&
    !hasRowsWithoutBrand;
  const canSubmit = !hasInvalidRows;

  const saveBrandOverrides = () => {
    if (!canSubmit) {
      setHasAttemptedSubmit(true);
      return;
    }

    const rawOverrides = rows.reduce<Record<string, Array<string>>>((acc, row) => {
      if (!row.brand) {
        return acc;
      }

      acc[row.brand] = row.regions;
      return acc;
    }, {});

    onSet(rawOverrides);
  };

  return (
    <Dialog open={open} onClose={onRequestClose} scroll="paper" maxWidth="lg" fullWidth>
      <DialogTitle>{name}</DialogTitle>

      <DialogContent>
        <DialogContentText>{label}</DialogContentText>

        <Box mt={2}>
          <Button variant="text" onClick={addBrandEntry} disabled={!canAddBrandEntry}>
            Add brand entry
          </Button>
        </Box>

        {!rows.length && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No brand entries added yet.
          </Typography>
        )}

        <Stack spacing={3} mt={2}>
          {rows.map((row) => {
            const availableRegionOptions = getRegionOptionsForBrand(row.brand);
            const allowedRegionCodes = new Set(availableRegionOptions.map((region) => region.code));
            const selectedAllowedRegionCodes = row.regions.filter((regionCode) => allowedRegionCodes.has(regionCode));
            const selectedRegionCodeSet = new Set(selectedAllowedRegionCodes);
            const rowValidationState = getRowValidationState(row);
            const shouldShowValidation = hasAttemptedSubmit;
            const shouldShowBrandError = shouldShowValidation && rowValidationState.isBrandMissing;
            const shouldShowRegionError = shouldShowValidation && rowValidationState.isRegionMissing;
            const brandOptions =
              row.brand === LUXURY_ESCAPES && luxuryEscapesBrand
                ? [luxuryEscapesBrand, ...brandRegionOverrideBrands]
                : brandRegionOverrideBrands;

            return (
              <Box key={row.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl fullWidth error={shouldShowBrandError}>
                    <InputLabel id={`${row.id}-brand-label`}>Brand</InputLabel>
                    <Select
                      labelId={`${row.id}-brand-label`}
                      value={row.brand}
                      label="Brand"
                      onChange={(event) => updateBrand(row.id, event.target.value)}
                    >
                      {brandOptions.map((brand) => (
                        <MenuItem
                          key={brand.value}
                          value={brand.value}
                          disabled={
                            brand.value === LUXURY_ESCAPES ||
                            (selectedBrands.includes(brand.value) && row.brand !== brand.value)
                          }
                        >
                          {brand.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {shouldShowBrandError && <FormHelperText>Select a brand</FormHelperText>}
                  </FormControl>

                  <Button variant="text" color="error" onClick={() => removeBrandEntry(row.id)}>
                    Remove
                  </Button>
                </Stack>

                <Stack direction="row" spacing={2} mt={2}>
                  <Button variant="text" onClick={() => setAllRegionsForEntry(row.id, true)} disabled={!row.brand}>
                    Select all regions
                  </Button>
                  <Button variant="text" onClick={() => setAllRegionsForEntry(row.id, false)} disabled={!row.brand}>
                    Clear regions
                  </Button>
                </Stack>

                <Box mt={1} display="grid" gap={2} gridTemplateColumns="repeat(4, 1fr)">
                  {availableRegionOptions.map((region) => (
                    <FormControlLabel
                      key={`${row.id}-${region.code}`}
                      control={
                        <Checkbox
                          checked={selectedRegionCodeSet.has(region.code)}
                          onChange={(event) => toggleRegion(row.id, region.code, event.target.checked)}
                          sx={{ py: 0 }}
                        />
                      }
                      label={region.name}
                    />
                  ))}
                </Box>
                {!row.brand && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Select a brand to see its available regions
                  </Typography>
                )}
                {shouldShowRegionError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Select at least one region
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>

        {hasAttemptedSubmit && hasInvalidRows && (
          <FormHelperText error sx={{ mt: 2 }}>
            Fix the validation errors before saving.
          </FormHelperText>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onRequestClose} variant="text">
          Cancel
        </Button>
        <Button variant="contained" onClick={saveBrandOverrides}>
          {saveButton || 'Set Brand Overrides'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
